import {
  ProductFulfillmentMode,
  RuleConditionInputOperator,
  type Attribute,
  type ProductType,
} from '@nucleus-commerce/api-client/admin';

/**
 * What the value input renders for a field. `tag` is matched against each tag of the product:
 * equality on a whole tag, `contains` / `starts_with` on any part of one.
 */
export type RuleValueKind = 'text' | 'tag' | 'enum' | 'money' | 'number' | 'date' | 'boolean';

export interface RuleFieldOption {
  value: string;
  label: string;
}

export interface RuleFieldDef {
  /** The `field` the API expects: a product field or `attribute.<key>`. */
  id: string;
  group: 'product' | 'attribute';
  label: string;
  kind: RuleValueKind;
  /** Dictionary values for enum fields; the value input offers a select instead of free text. */
  options?: RuleFieldOption[];
}

export const PRODUCT_RULE_FIELDS = [
  'product_type',
  'tag',
  'vendor',
  'title',
  'price_cents',
  'compare_at_cents',
  'fulfillment_mode',
  'created_at',
] as const;

export type ProductRuleField = (typeof PRODUCT_RULE_FIELDS)[number];

const PRODUCT_FIELD_KIND: Record<ProductRuleField, RuleValueKind> = {
  product_type: 'enum',
  tag: 'tag',
  vendor: 'text',
  title: 'text',
  price_cents: 'money',
  compare_at_cents: 'money',
  fulfillment_mode: 'enum',
  created_at: 'date',
};

/** A dimension is matched on its number (whatever the unit), rich text on its text without markup. */
const ATTRIBUTE_KIND: Record<Attribute['dataType'], RuleValueKind> = {
  select: 'enum',
  multiselect: 'enum',
  text: 'text',
  number: 'number',
  boolean: 'boolean',
  dimension: 'number',
  rich_text: 'text',
};

/**
 * Operators the evaluator can satisfy per kind. A date value is a store day: `equals` means
 * within that day, `gt` after it ends, `lte` up to its end.
 */
export const OPERATORS_BY_KIND: Record<RuleValueKind, RuleConditionInputOperator[]> = {
  text: ['equals', 'not_equals', 'contains', 'starts_with', 'in', 'not_in'],
  tag: ['equals', 'not_equals', 'contains', 'starts_with', 'in', 'not_in'],
  enum: ['equals', 'not_equals', 'in', 'not_in'],
  money: ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte'],
  number: ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte'],
  date: ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte'],
  boolean: ['equals'],
};

export const RULE_OPERATORS = Object.values(RuleConditionInputOperator);

export const isListOperator = (operator: string): boolean =>
  operator === 'in' || operator === 'not_in';

export const isProductRuleField = (field: string): field is ProductRuleField =>
  (PRODUCT_RULE_FIELDS as readonly string[]).includes(field);

export interface BuildRuleFieldsInput {
  productTypes: readonly ProductType[];
  attributes: readonly Attribute[];
  productFieldLabel: (field: ProductRuleField) => string;
  fulfillmentModeLabel: (mode: ProductFulfillmentMode) => string;
}

/** Product fields first, then every attribute (select ones carry their dictionary values). */
export function buildRuleFields({
  productTypes,
  attributes,
  productFieldLabel,
  fulfillmentModeLabel,
}: BuildRuleFieldsInput): RuleFieldDef[] {
  const productFields = PRODUCT_RULE_FIELDS.map((id): RuleFieldDef => {
    const base: RuleFieldDef = {
      id,
      group: 'product',
      label: productFieldLabel(id),
      kind: PRODUCT_FIELD_KIND[id],
    };
    if (id === 'product_type') {
      return { ...base, options: productTypes.map((p) => ({ value: p.slug, label: p.name })) };
    }
    if (id === 'fulfillment_mode') {
      return {
        ...base,
        options: Object.values(ProductFulfillmentMode).map((mode) => ({
          value: mode,
          label: fulfillmentModeLabel(mode),
        })),
      };
    }
    return base;
  });
  const attributeFields = attributes.map((attribute): RuleFieldDef => {
    const kind = ATTRIBUTE_KIND[attribute.dataType];
    return {
      id: `attribute.${attribute.key}`,
      group: 'attribute',
      label: attribute.label,
      kind,
      ...(kind === 'enum'
        ? { options: attribute.values.map((v) => ({ value: v.value, label: v.label })) }
        : {}),
    };
  });
  return [...productFields, ...attributeFields];
}

/** Kind of a field even when the dictionary has not loaded: product fields are static, attributes default to text. */
export function kindOfField(field: string, defs: readonly RuleFieldDef[]): RuleValueKind {
  const def = defs.find((d) => d.id === field);
  if (def) return def.kind;
  return isProductRuleField(field) ? PRODUCT_FIELD_KIND[field] : 'text';
}

export function operatorsFor(kind: RuleValueKind): RuleConditionInputOperator[] {
  return OPERATORS_BY_KIND[kind];
}

/** The value a fresh or re-typed condition starts with: a list for in / not_in, "true" for booleans. */
export function defaultRuleValue(kind: RuleValueKind, operator: string): string | string[] {
  if (isListOperator(operator)) return [];
  return kind === 'boolean' ? 'true' : '';
}
